import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

export type StrictnessLevel = 'lenient' | 'standard' | 'strict';

interface StrictnessSelectorProps {
  /** Show the pricing-line paragraph. Only the calibration panel persists the key
   *  pricing reads (metadata.calibration_strictness); the template editor writes a
   *  different key, so there the paragraph would promise something that cannot happen. */
  showPricingEffect?: boolean;
  value: StrictnessLevel;
  onChange: (value: StrictnessLevel) => void;
}

const OPTIONS: {
  level: StrictnessLevel;
  label: string;
  description: string;
  color: string;
  selectedBg: string;
  selectedBorder: string;
  barColor: string;
  barWidth: string;
  icon: string;
}[] = [
  {
    level: 'lenient',
    label: 'Lenient',
    description: 'Forgiving — great for beginners and rehab',
    color: 'text-emerald-400',
    selectedBg: 'bg-emerald-500/10',
    selectedBorder: 'border-emerald-500/40',
    barColor: 'bg-emerald-500',
    barWidth: 'w-1/3',
    icon: '◠',
  },
  {
    level: 'standard',
    label: 'Standard',
    description: 'Balanced — suits most coaching scenarios',
    color: 'text-sky-400',
    selectedBg: 'bg-sky-500/10',
    selectedBorder: 'border-sky-500/40',
    barColor: 'bg-sky-500',
    barWidth: 'w-2/3',
    icon: '—',
  },
  {
    level: 'strict',
    label: 'Strict',
    description: 'Demanding — for competitive technique work',
    color: 'text-amber-400',
    selectedBg: 'bg-amber-500/10',
    selectedBorder: 'border-amber-500/40',
    barColor: 'bg-amber-500',
    barWidth: 'w-full',
    icon: '◡',
  },
];

export default function StrictnessSelector({ value, onChange, showPricingEffect = false }: StrictnessSelectorProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="space-y-3">
      {/* Label row */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-text-secondary">
          Scoring Strictness
        </label>
        <div className="relative">
          <button
            type="button"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
            className="text-text-muted hover:text-text-secondary transition-colors"
            aria-label="What does strictness control?"
          >
            <InformationCircleIcon className="h-4 w-4" />
          </button>
          {showTooltip && (
            <div className="absolute left-6 top-1/2 -translate-y-1/2 z-50 w-72 px-3 py-2.5 rounded-lg bg-dark-elevated-1 border border-border-subtle shadow-xl text-xs text-text-secondary leading-relaxed">
              <p className="font-medium text-text-primary mb-1">How strictness works</p>
              <p>
                Controls how precisely athletes must match this template.
                Strictness scales the scoring tolerance — stricter means smaller
                deviations cause bigger score drops. Gates (auto-fail rules) are
                not affected.
              </p>
              {showPricingEffect && (
                <p className="mt-2">
                  When this template&apos;s &quot;too far off to grade&quot; line is
                  switched on, strictness moves that line too:{' '}
                  <span className="text-emerald-400">Lenient</span> keeps it further
                  from your good example videos, so fewer attempts get marked too far
                  off. <span className="text-sky-400">Standard</span> keeps the line
                  as learned. <span className="text-amber-400">Strict</span> moves it
                  closer, so more attempts can be marked too far off.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Three option cards */}
      <div className="grid grid-cols-3 gap-2">
        {OPTIONS.map((opt) => {
          const isSelected = value === opt.level;
          return (
            <button
              key={opt.level}
              type="button"
              onClick={() => onChange(opt.level)}
              className={`
                relative flex flex-col items-start gap-2 px-3.5 py-3 rounded-lg
                border transition-all duration-150 text-left
                ${isSelected
                  ? `${opt.selectedBg} ${opt.selectedBorder} ring-1 ring-${opt.level === 'lenient' ? 'emerald' : opt.level === 'standard' ? 'sky' : 'amber'}-500/20`
                  : 'bg-dark-elevated-2 border-border-subtle hover:border-border-default hover:bg-dark-elevated-1'
                }
              `}
            >
              {/* Label */}
              <span className={`text-sm font-semibold ${isSelected ? opt.color : 'text-text-primary'}`}>
                {opt.label}
              </span>

              {/* Description */}
              <span className="text-[11px] leading-tight text-text-muted">
                {opt.description}
              </span>

              {/* Tolerance bar — visual metaphor */}
              <div className="w-full mt-1">
                <div className="h-1 w-full rounded-full bg-white/5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      isSelected ? opt.barColor : 'bg-white/10'
                    } ${opt.barWidth}`}
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-text-muted">tight</span>
                  <span className="text-[9px] text-text-muted">wide</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
